import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, LogIn, TrendingUp, Shield, BarChart3, Zap } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const Landing = ({ onLoginSuccess }) => {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: '', type: '' });
        }, 3000);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast('Please fix the errors in the form', 'error');
            return;
        }

        setIsLoading(true);

        setTimeout(() => {
            if (formData.email === 'admin@viewwiser.com' && formData.password === 'admin123') {
                showToast('Login successful! Redirecting...', 'success');
                setTimeout(() => {
                    setIsLoading(false);
                    setIsLoggedIn(true);
                    sessionStorage.setItem('isLoggedIn', 'true');
                    if (onLoginSuccess) {
                        onLoginSuccess();
                    }
                    window.location.href = '/dashboard';
                }, 1500);
            } else {
                setIsLoading(false);
                showToast('Invalid email or password', 'error');
            }
        }, 1000);
    };

    return (
        <>
            <Header />

            {/* Toast Notification */}
            {toast.show && (
                <div className={`fixed top-6 right-6 z-50 px-6 py-4 rounded-lg shadow-2xl transform transition-all duration-300 ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                    }`}>
                    <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                        <p className="font-medium">{toast.message}</p>
                    </div>
                </div>
            )}

            <div className="min-h-screen ">
                <div className=" px-4 py-10">
                    <div className="grid lg:grid-cols-2 gap-10 items-center">

                        {/* Left Side - Welcome Text & Features */}
                        <div className="space-y-8 ml-20 ">
                            <div>
                                <h1 className="text-5xl font-bold text-slate-800 mb-4">
                                    Welcome to FinanceHub
                                </h1>
                                <p className="text-xl text-slate-600 leading-relaxed">
                                    Your complete financial management solution. Track cash flow,
                                    manage payables, and make data-driven decisions with powerful analytics.
                                </p>
                            </div>

                            {/* Features Grid */}
                            <div className="grid sm:grid-cols-2 gap-6 ">
                                <div className="bg-white rounded-xl shadow-lg p-6 bg-gradient-to-br from-blue-50 to-blue-100">
                                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                                        <BarChart3 className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-800 mb-2">
                                        Real-time Analytics
                                    </h3>
                                    <p className="text-sm text-slate-600">
                                        Get instant insights into your financial performance
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl shadow-lg p-6 bg-white rounded-xl shadow-lg p-6 bg-gradient-to-br from-green-50 to-green-100">
                                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                                        <TrendingUp className="w-6 h-6 text-green-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-800 mb-2">
                                        Cash Flow Tracking
                                    </h3>
                                    <p className="text-sm text-slate-600">
                                        Monitor income and expenses with detailed reports
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl shadow-lg p-6 bg-gradient-to-br from-purple-50 to-purple-100">
                                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                                        <Shield className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-800 mb-2">
                                        Secure & Reliable
                                    </h3>
                                    <p className="text-sm text-slate-600">
                                        Bank-grade security to protect your financial data
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl shadow-lg p-6 bg-gradient-to-br from-orange-50 to-orange-100">
                                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                                        <Zap className="w-6 h-6 text-orange-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-800 mb-2">
                                        Fast & Efficient
                                    </h3>
                                    <p className="text-sm text-slate-600">
                                        Quick setup and intuitive dashboard interface
                                    </p>
                                </div>

                            </div>
                            {/* Stats Section */}
                            <div className="py-10">
                                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-8 text-white">
                                    <div className="grid grid-cols-3 gap-6 text-center">
                                        <div>
                                            <p className="text-3xl font-bold mb-1">10K+</p>
                                            <p className="text-sm text-blue-100">Active Users</p>
                                        </div>
                                        <div>
                                            <p className="text-3xl font-bold mb-1">50+</p>
                                            <p className="text-sm text-blue-100">Countries</p>
                                        </div>
                                        <div>
                                            <p className="text-3xl font-bold mb-1">99.9%</p>
                                            <p className="text-sm text-blue-100">Uptime</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Login Form */}
                        <div className="lg:sticky lg:top-24 ml-20">
                            <div className=" w-150 h-auto max-w-lg rounded-xl shadow-2xl p-6 bg-gradient-to-br from-purple-50 to-purple-100">
                                <div className="text-center mb-6">
                                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Sign In</h2>
                                    <p className="text-slate-600">Access your dashboard</p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    {/* Email Field */}
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2 text-left">
                                            Email Address
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Mail className="h-5 w-5 text-slate-400" />
                                            </div>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={`block w-full pl-10 pr-3 py-3 border ${errors.email ? 'border-red-500' : 'border-slate-300'
                                                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                                                placeholder="you@example.com"
                                            />
                                        </div>
                                        {errors.email && (
                                            <p className="mt-2 text-sm text-red-600 text-left">{errors.email}</p>
                                        )}
                                    </div>

                                    {/* Password Field */}
                                    <div>
                                        <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2 text-left">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Lock className="h-5 w-5 text-slate-400" />
                                            </div>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                id="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                className={`block w-full pl-10 pr-12 py-3 border ${errors.password ? 'border-red-500' : 'border-slate-300'
                                                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
                                                placeholder="Enter your password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                                                ) : (
                                                    <Eye className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                                                )}
                                            </button>
                                        </div>
                                        {errors.password && (
                                            <p className="mt-2 text-sm text-red-600 text-left">{errors.password}</p>
                                        )}
                                    </div>

                                    {/* Remember & Forgot */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <input
                                                id="remember"
                                                type="checkbox"
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                                            />
                                            <label htmlFor="remember" className="ml-2 block text-sm text-slate-700">
                                                Remember me
                                            </label>
                                        </div>
                                        <a href="#" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                                            Forgot password?
                                        </a>
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className={`w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors shadow-lg ${isLoading ? 'opacity-70 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        {isLoading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Signing in...</span>
                                            </>
                                        ) : (
                                            <>
                                                <LogIn className="w-5 h-5" />
                                                <span>Sign In</span>
                                            </>
                                        )}
                                    </button>
                                </form>

                                {/* Demo Credentials */}
                                <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                                    <p className="text-xs font-semibold text-slate-600 mb-2">Demo Credentials:</p>
                                    <p className="text-xs text-slate-600">Email: admin@viewwiser.com</p>
                                    <p className="text-xs text-slate-600">Password: admin123</p>
                                </div>

                                {/* Register Link */}
                                <div className="mt-6 text-center">
                                    <p className="text-sm text-slate-600">
                                        Don't have an account?{' '}
                                        <a href="#" className="text-blue-600 hover:text-blue-700 font-medium">
                                            Sign up
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default Landing;